import React from 'react'
import moment from 'moment'

import SaveIcon from '@mui/icons-material/Save'

import Alert from '@mui/material/Alert'
import Autocomplete from '@mui/material/Autocomplete'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import FormControlLabel from '@mui/material/FormControlLabel'
import Stack from '@mui/material/Stack'
import Switch from '@mui/material/Switch'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'

import { getBaseLocationLabel, ON_CALL_TEAMS } from './globalRosterUtils'

export const NewRosterDialog = ({
  open,
  onClose,
  rosterForm,
  setRosterForm,
  baseLocationOptions,
  rosterSaving,
  saveRoster,
}) => (
  <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
    <DialogTitle>New Rota Plan</DialogTitle>
    <DialogContent dividers>
      <Stack gap={2} pt={1}>
        <TextField
          label="Title"
          value={rosterForm.title}
          onChange={(e) =>
            setRosterForm((prev) => ({ ...prev, title: e.target.value }))
          }
          autoComplete="off"
          fullWidth
          required
          helperText={`This will identify the draft planning bucket (e.g. 'on-call ${moment().format('MMMM')}')`}
        />
        <Autocomplete
          options={baseLocationOptions ?? []}
          value={rosterForm.baseLocation}
          onChange={(e, value) =>
            setRosterForm((prev) => ({ ...prev, baseLocation: value }))
          }
          getOptionLabel={getBaseLocationLabel}
          isOptionEqualToValue={(a, b) => a?._id === b?._id}
          renderInput={(params) => (
            <TextField {...params} label="Base location" />
          )}
        />
        <TextField
          label="Description"
          value={rosterForm.description}
          onChange={(e) =>
            setRosterForm((prev) => ({
              ...prev,
              description: e.target.value,
            }))
          }
          fullWidth
          multiline
          minRows={3}
        />
        <Stack>
          <FormControlLabel
            control={
              <Switch
                checked={!!rosterForm.isOnCall}
                onChange={(e) =>
                  setRosterForm((prev) => ({
                    ...prev,
                    isOnCall: e.target.checked,
                  }))
                }
              />
            }
            label="On-call rota plan"
          />
          <Typography variant="caption" color="text.secondary">
            On-call cover sits alongside normal duties as a standby layer.
          </Typography>
        </Stack>
        {!!rosterForm.isOnCall && (
          <Alert severity="info">
            Only staff in the {ON_CALL_TEAMS.join(' or ')} teams can be
            assigned. Overlaps with other shifts and approved leave are shown as
            warnings but won&apos;t block assignment, staff cannot book these
            shifts themselves, and on-call shifts appear with dashed borders on
            the global roster.
          </Alert>
        )}
      </Stack>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose}>Cancel</Button>
      <Button
        startIcon={<SaveIcon />}
        variant="contained"
        disabled={rosterSaving || !rosterForm.title}
        onClick={saveRoster}
      >
        Save Rota Plan
      </Button>
    </DialogActions>
  </Dialog>
)

export const ArchiveRosterDialog = ({
  open,
  onClose,
  selectedRoster,
  archiveRosterError,
  archiveRoster,
}) => (
  <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
    <DialogTitle>Archive Rota Plan</DialogTitle>
    <DialogContent dividers>
      <Stack gap={2}>
        {archiveRosterError && (
          <Alert severity="error">{archiveRosterError}</Alert>
        )}
        <Alert severity="warning">
          Archiving hides this draft rota plan from the selector and removes its
          unpublished shifts. Rota plans with verified or paid shifts cannot be
          archived.
        </Alert>
        <Typography>
          Are you sure you want to archive {selectedRoster?.title}?
        </Typography>
      </Stack>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose}>Cancel</Button>
      <Button color="error" variant="contained" onClick={archiveRoster}>
        Archive Rota Plan
      </Button>
    </DialogActions>
  </Dialog>
)

export const PublishRosterDialog = ({
  open,
  onClose,
  selectedRoster,
  publishSummary,
  publishRoster,
  publishing,
}) => {
  const shiftCount = publishSummary?.shiftCount ?? 0
  const assignedShiftCount = publishSummary?.assignedShiftCount ?? 0

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Publish Rota Plan</DialogTitle>
      <DialogContent dividers>
        <Stack gap={2}>
          <Alert severity="info">
            Publishing will make {shiftCount} shift
            {shiftCount === 1 ? '' : 's'} live on the Global Roster and notify
            assigned staff.
          </Alert>
          <Typography>
            {assignedShiftCount} assigned shift
            {assignedShiftCount === 1 ? '' : 's'} will require staff
            acknowledgement. Vacant shifts will remain available in the normal
            roster workflow.
          </Typography>
          <Alert severity="warning">
            Once published, {selectedRoster?.title || 'this rota plan'} is no
            longer a draft plan and will disappear from the rota plan selector.
            Its shifts stay on the roster as normal live shifts, which you can
            still open and edit individually.
          </Alert>
          <Typography>
            Are you sure you want to publish {selectedRoster?.title}?
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={publishRoster}
          disabled={publishing || !publishSummary}
        >
          Publish Rota Plan
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// Shown once, straight after a successful publish - users were raising bug
// reports because the plan "vanished" from the selector, so spell out where
// everything went.
export const RosterPublishedDialog = ({ open, onClose, publishedRoster }) => (
  <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
    <DialogTitle>Rota Plan Published</DialogTitle>
    <DialogContent dividers>
      <Stack gap={2}>
        <Alert severity="success">
          {publishedRoster?.title || 'Your rota plan'} is now live.
        </Alert>
        <Stack component="ul" gap={1} sx={{ pl: 3, my: 0 }}>
          <Typography component="li">
            Its shifts now appear on the Global Roster as live shifts, without
            the draft highlight.
          </Typography>
          <Typography component="li">
            Assigned staff have been notified and asked to acknowledge their
            shifts.
          </Typography>
          <Typography component="li">
            The plan has left draft planning, so it no longer appears in the
            rota plan selector - once published, it isn&apos;t a plan any more;
            its shifts are the live rota.
          </Typography>
          <Typography component="li">
            To make further changes, click any shift on the roster to edit it
            directly.
          </Typography>
        </Stack>
      </Stack>
    </DialogContent>
    <DialogActions>
      <Button variant="contained" onClick={onClose}>
        Got It
      </Button>
    </DialogActions>
  </Dialog>
)
